import React, {useEffect, useState, useCallback, useMemo} from "react";
import {fetchAllUsers, fetchUserDownloadLog} from "../api";
import AuditLogDialog from "./AuditLogDialog";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  Skeleton,
  TablePagination,
  Tooltip
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import {getErrorMessage, formatDate} from "../utils";
import {PORTAL_CHIP, PORTAL_GRADIENT, PORTAL_HEADER_ICON} from "./AdminStyles";

const COLUMNS = [
  {id: "display_name", label: "Name"},
  {id: "email", label: "Email"},
  {id: "username", label: "Username"},
  {id: "ip_address", label: "IP Address"},
  {id: "created_at", label: "Created At"},
  {id: "last_login_at", label: "Last Login At"},
  {id: "actions", label: "", sortable: false},
];

function descendingComparator(a, b, orderBy) {
  const av = a?.[orderBy] ?? "";
  const bv = b?.[orderBy] ?? "";
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function UsersCRUD() {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isErrorData, setIsErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [bannerError, setBannerError] = useState("");
  const [toast, setToast] = useState({open: false, severity: "info", message: ""});
  
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("last_login_at");
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [logUser, setLogUser] = useState(null);
  const [logData, setLogData] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState("");
  const [logFilters, setLogFilters] = useState({date: "", bucket: "", file: ""});
  
  const showToast = (severity, message) => setToast({open: true, severity, message});
  const closeToast = () => setToast((t) => ({...t, open: false}));
  
  const fetchRecordsData = useCallback(async () => {
    setIsErrorData(false);
    setBannerError("");
    try {
      const response = await fetchAllUsers();
      const formatted = (response?.data ?? []).map((u) => ({
        ...u,
        created_at: formatDate(u.created_at),
        last_login_at: formatDate(u.last_login_at),
      }));
      setData(formatted);
    } catch (error) {
      setIsErrorData(true);
      setData([]);
      const msg = getErrorMessage(error, "Error loading users");
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsLoadingData(false);
    }
  }, []);
  
  useEffect(() => {
    fetchRecordsData();
  }, [fetchRecordsData]);
  
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter(
      (item) =>
        item.display_name?.toLowerCase().includes(lower) ||
        item.email?.toLowerCase().includes(lower) ||
        item.created_at?.toLowerCase().includes(lower) ||
        item.last_login_at?.toLowerCase().includes(lower)
    );
  }, [data, searchTerm]);
  
  const sortedData = useMemo(
    () => [...filteredData].sort(getComparator(order, orderBy)),
    [filteredData, order, orderBy]
  );
  
  useEffect(() => {
    setPage(0);
  }, [searchTerm, order, orderBy]);
  
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4].map((i) => (
        <TableRow key={i}>
          {COLUMNS.map((c) => (
            <TableCell key={c.id}><Skeleton variant="text" width={140}/></TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
  
  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={COLUMNS.length} sx={{py: 8, textAlign: "center"}}>
        <PeopleIcon sx={{fontSize: 48, color: "#bdbdbd", mb: 1}}/>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Users Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {searchTerm ? "No results match your search criteria." : "There are no users to display."}
        </Typography>
      </TableCell>
    </TableRow>
  );
  
  const openLogDialog = async (user) => {
    setLogUser(user);
    setLogDialogOpen(true);
    setLogLoading(true);
    setLogError("");
    setLogData([]);
    setLogFilters({date: "", bucket: "", file: ""});
    try {
      const res = await fetchUserDownloadLog(user.id);
      setLogData(res?.data ?? []);
    } catch (error) {
      const msg = getErrorMessage(error, "Error loading download logs");
      setLogError(msg);
      showToast("error", msg);
    } finally {
      setLogLoading(false);
    }
  };
  
  const closeLogDialog = () => {
    setLogDialogOpen(false);
    setLogUser(null);
  };
  
  return (
    <Box sx={{maxWidth: 1400, mx: "auto", p: 1}}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background: PORTAL_GRADIENT,
          color: "white",
        }}
      >
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PeopleIcon sx={{fontSize: 28, color: PORTAL_HEADER_ICON}}/>
              <Typography variant="h6" fontWeight={700}>
                Users
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{opacity: 0.9}}>
              Users accessed the QLI platform and their download history
            </Typography>
          </Box>
        </Stack>
      </Paper>
      
      {/* Error Banner */}
      {(bannerError || isErrorData) && (
        <Alert severity="error" sx={{mb: 3, borderRadius: 2}} onClose={() => setBannerError("")}>
          {bannerError || "Error loading data"}
        </Alert>
      )}
      
      {/* Search Bar */}
      <Paper elevation={0} sx={{p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <TextField
            placeholder="Search by name, email..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{minWidth: 300}}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon color="action"/></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}><CloseIcon fontSize="small"/></IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredData.length} ${filteredData.length === 1 ? "user" : "users"}`} color="primary"
                variant="outlined"/>
        </Stack>
      </Paper>
      
      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: "#f5f5f5"}}>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{fontWeight: 700}}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  {col.sortable === false ? (
                    col.label
                  ) : (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : "asc"}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoadingData ? (
            <TableSkeleton/>
          ) : filteredData.length === 0 ? (
            <TableBody><EmptyState/></TableBody>
          ) : (
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow
                  key={item.id ?? item.email}
                  sx={{
                    "&:hover": {backgroundColor: "#fafafa"},
                    "&:nth-of-type(even)": {backgroundColor: "#fcfcfc"},
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={<PersonIcon sx={{fontSize: 16}}/>}
                      label={item.display_name || "-"}
                      size="small"
                      sx={PORTAL_CHIP}
                    />
                  </TableCell>
                  <TableCell><Typography variant="body2">{item.email || "-"}</Typography></TableCell>
                  <TableCell>
                    {item.username && item.username !== '-' ? (
                      <Typography
                        variant="body2"
                        component="a"
                        href={`https://photoph.qualcomm.com/People?field=uid&match=eq&query=${encodeURIComponent(item.username)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "#1565c0",
                          textDecoration: "none",
                          fontWeight: 500,
                          "&:hover": {textDecoration: "underline"},
                        }}
                      >
                        {item.username}
                      </Typography>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2">{item.ip_address || "-"}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{item.created_at || "-"}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{item.last_login_at || "Never"}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title="View download history">
                      <IconButton size="small" onClick={() => openLogDialog(item)}>
                        <HistoryIcon fontSize="small" sx={{color: "#3f51b5"}}/>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        <Box sx={{display: "flex", justifyContent: "flex-end", mt: 1}}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                gap: 1,
                minHeight: 48,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                margin: 0,
                alignSelf: "center",
              },
              "& .MuiTablePagination-actions": {
                marginLeft: 1,
              },
            }}
          />
        </Box>
      </TableContainer>
      
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast}
                anchorOrigin={{vertical: "top", horizontal: "center"}}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{width: "100%", borderRadius: 2}}>
          {toast.message}
        </Alert>
      </Snackbar>
      
      <AuditLogDialog
        open={logDialogOpen}
        onClose={closeLogDialog}
        user={logUser}
        logs={logData}
        loading={logLoading}
        error={logError}
        filters={logFilters}
        setFilters={setLogFilters}
      />
    
    </Box>
  );
}